// @flow

import React, { Component } from "react";
import Slider from "./Slider";
import AnswerSlider from "./AnswerSlider";
import Button from "./Button";
import Progress from "./Progress";
import Emoji from "./Emoji";
import BouncyText from "./BouncyText";

type QuestionProps = {
  step: number,
  question: string,
  answerValue: number,
  commit: (value: number) => void,
  nextStep: string,
  totalSteps: number
};

type QuestionState = {
  value: number | null,
  submitted: boolean,
  answerAnimationDone: boolean
};

export default class Question extends Component<
  void,
  QuestionProps,
  QuestionState
> {
  state = {
    value: null,
    submitted: false,
    answerAnimationDone: false
  };

  updateValue = (value: number) => {
    this.setState((state: QuestionState): QuestionState => ({
      ...state,
      value
    }));
  };

  submit = () => {
    const { value } = this.state;
    const { answerValue } = this.props;
    this.setState((state: QuestionState): QuestionState => ({
      ...state,
      submitted: true,
      answerAnimationDone: value === answerValue
    }));
  };

  answerAnimationDone = () => {
    this.setState((state: QuestionState): QuestionState => ({
      ...state,
      answerAnimationDone: true
    }));
  };

  commit = () => {
    if (this.state.value !== null) {
      this.props.commit(this.state.value);
    }
  };

  render() {
    const { answerAnimationDone, value, submitted } = this.state;
    const { step, question, answerValue, nextStep, totalSteps } = this.props;

    return (
      <div className="question">
        <Progress
          text={submitted ? "Answer" : "Question"}
          step={step}
          totalSteps={totalSteps}
          showText
        />
        <div className="title">
          How many millennials …<br />
          {question}
        </div>
        {submitted && value !== null
          ? <Answer
              guessedValue={value}
              actualValue={answerValue}
              onAnimationDone={this.answerAnimationDone}
              animationDone={answerAnimationDone}
              commit={this.commit}
              finalQuestion={nextStep === "outro"}
            />
          : <Guess
              value={value}
              updateValue={this.updateValue}
              submit={this.submit}
              started={value !== null}
            />}

        <style jsx>{`
          .question {
            box-sizing: border-box;
            display: flex;
            flex-direction: column;
            justify-content: space-between;
            align-items: center;
            max-height: 100vh;
            height: 500px;
            padding: 72px 20px 20px 20px;
            width: 100%;
          }
          .title {
            box-sizing: border-box;
            font-family: FrutigerLTCom;
            font-weight: 300;
            font-size: 18px;
            line-height: 20px;
            text-align: center;
            height: 104px;
            min-height: 104px;
            padding-bottom: 24px;
            width: 100%;
            max-width: 630px;
          }
          @media screen and (min-width: 480px) {
            .title {
              font-size: 24px;
              line-height: 26px;
              height: 102px;
              min-height: 102px;
            }
          }
        `}</style>
      </div>
    );
  }
}

const Guess = ({ value, updateValue, submit, started }) =>
  <div className="guess">
    <div className="slider">
      <Slider
        min={0}
        max={100}
        value={value}
        onChange={updateValue}
        unit="%"
        color="#3C8287"
      />
    </div>
    <div className="controls">
      {started && <Button label="Submit answer" onClick={submit} />}
    </div>
    <style jsx>{`
      .guess {
        width: 100%;
        height: 100%;
        position: relative;
        display: flex;
        flex-direction: column;
        justify-content: flex-end;
        align-items: center;
      }
      .slider {
        position: absolute;
        top: 24px;
        left: 0;
        width: 100%;
      }
      .controls {
        max-width: 320px;
        display: flex;
        flex-direction: column;

        justify-content: center;
      }
    `}</style>
  </div>;

const AnswerFeedback = ({ diff }) =>
  <div>
    {diff < 10
      ? <BouncyText items={["Spot ", "on", "! ", <Emoji name="nerd" />]} />
      : diff < 25
        ? <BouncyText items={["Nice", "! ", <Emoji name="blush" />]} />
        : diff < 50
          ? <BouncyText
              items={["Not ", "great", "! ", <Emoji name="persevere" />]}
            />
          : <BouncyText
              items={[
                "Not ",
                "even ",
                "close",
                "! ",
                <Emoji name="scream_cat" />
              ]}
            />}
    <style jsx>{`
      div {
        text-align: center;
        font-family: FrutigerLTCom;
        font-weight: 700;
        font-size: 18px;
      }
    `}</style>
  </div>;

type AnswerProps = {
  guessedValue: number,
  actualValue: number,
  onAnimationDone: () => void,
  animationDone: boolean,
  commit: () => void,
  finalQuestion: boolean
};

const Answer = ({
  guessedValue,
  actualValue,
  onAnimationDone,
  animationDone,
  commit,
  finalQuestion
}: AnswerProps) =>
  <div className="answer">
    <div className="slider">
      <AnswerSlider
        min={0}
        max={100}
        guessedValue={guessedValue}
        value={actualValue}
        unit="%"
        color="#3C8287"
        t={s => s}
        onComplete={onAnimationDone}
      />
    </div>
    <div className="controls">
      <div className="feedback">
        {animationDone &&
          <AnswerFeedback diff={Math.abs(actualValue - guessedValue)} />}
      </div>
      {animationDone &&
        <Button
          label={finalQuestion ? "See results" : "Next question"}
          onClick={commit}
        />}
    </div>
    <style jsx>{`
      .answer {
        width: 100%;
        height: 100%;
        position: relative;
        display: flex;
        flex-direction: column;
        justify-content: flex-end;
        align-items: center;
      }
      .slider {
        position: absolute;

        top: 24px;
        left: 0;
        width: 100%;
      }
      .feedback {
        margin-bottom: 32px;
      }
      .controls {
        max-width: 320px;
        display: flex;
        flex-direction: column;
        justify-content: center;
      }
    `}</style>
  </div>;



// WEBPACK FOOTER //
// ./src/components/Quiz/Question.js